import { cancel, call, delay, fork, put, take, takeEvery, select, all } from 'redux-saga/effects';
import {
  handleWebsocketKeepAlive,
  RECONNECT_TIMEOUT,
  sendMessage,
  subscriptionChannel,
  websocketInitChannel
} from './helpers';
import { actions, WebsocketState, actions as websocketActions } from './reducer';
import { Connection } from '../connection';
import { connect, socketObservable } from '../main';
import { getWebsocket } from './selectors';
import { IWebsocketMessageSubscription } from 'types';
import { Observable } from 'rxjs';

function* handleWebsocketSubscribe(action: any): any {
  try {
    const websocketState: WebsocketState = yield select(getWebsocket);

    if (!websocketState.connected) {
      yield take(['CONNECTION_SUCCESS']);
    }
    let subkey = action.payload?.key;
    const subscriptionHandler = action.payload?.handler;

    subkey = JSON.parse(subkey);
    if (!subkey) {
      throw new Error('No key passed through to subscribe to');
    }
    const subscriptionMessage: IWebsocketMessageSubscription = {
      action: 'subscribe',
      ...subkey
    };
    // send subscription message to server
    yield put(
      websocketActions.send({
        ...subscriptionMessage
      })
    );

    // configure subscription listener
    const websocketSub: Observable<unknown> | undefined = socketObservable(subscriptionMessage);
    if (websocketSub === undefined) {
      console.log('failed to register websocket');
      return;
    }
    const channel = yield call(subscriptionChannel, websocketSub, subscriptionHandler);
    try {
      while (true) {
        const action = yield take(channel);
        yield put(action);
      }
    } finally {
      channel.close();
    }
  } catch (err) {
    console.log(`Error: failed to register websocket`, err);
  }
}

function* handleWebsocketUnsubscribe(action: any): any {
  try {
    const key = action.payload?.key;
    if (!key) {
      throw new Error('No key passed through to subscribe to!');
    }
    const subkey = JSON.parse(key);
    const unsubscribeMessage: IWebsocketMessageSubscription = {
      action: 'unsubscribe',
      ...subkey
    };
    // send subscription message to server
    yield put(
      websocketActions.send({
        ...unsubscribeMessage
      })
    );
  } catch (err) {
    console.log('Unable to unsubscribe', JSON.stringify(action));
  }
}

function* watchWebsocketSubscribe() {
  yield takeEvery('SUBSCRIBE_TO_WEBSOCKET', handleWebsocketSubscribe);
}

function* watchWebsocketUnsubscribe() {
  yield takeEvery('UNSUBSCRIBE_FROM_WEBSOCKET', handleWebsocketUnsubscribe);
}

function* watchWSConnect() {
  yield takeEvery('CONNECTION_SUCCESS', handleWebsocketKeepAlive);
}
// HOC wrapping saga with connection auth data and ws url
export function getMainSaga(wsUrl: string, idToken: string, actionsToWaitFor?: Array<string>) {
  return function* watchWebsocket(): any {
    if (actionsToWaitFor) {
      yield take(actionsToWaitFor);
    }

    yield all([fork(watchWebsocketSubscribe)]);
    yield all([fork(watchWebsocketUnsubscribe)]);
    yield all([fork(watchWSConnect)]);

    const connection: Connection = yield connect(wsUrl, idToken);
    const channel = yield call(websocketInitChannel, connection.ws!);
    const sendMessageTask = yield fork(sendMessage, connection.ws!);

    yield put(actions.connectionSuccess());

    try {
      while (true) {
        const action = yield take(channel);
        yield put(action);
      }
    } finally {
      // cancel background tasks...
      channel.close();
      yield cancel(sendMessageTask);
      // ...and start again
      yield delay(RECONNECT_TIMEOUT);
      return yield call(getMainSaga, wsUrl, idToken, actionsToWaitFor!);
    }
  };
}

// for testing - TODO refactor
export default function* saga(): any {
  const connection = new WebSocket(`ws://localhost:8080`);
  const channel = yield call(websocketInitChannel, connection);
  yield put(actions.connectionSuccess());
  const sendMessageTask = yield fork(sendMessage, connection);
  try {
    while (true) {
      const action = yield take(channel);
      yield put(action);
    }
  } finally {
    // cancel background tasks...
    channel.close();
    yield cancel(sendMessageTask);
    connection.close();
  }
}
